import { useState, useEffect } from 'react';
import { Style, Circle, Fill, Stroke } from 'ol/style';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import { IoMove as MoveIcon } from 'react-icons/io5';
import { BiShapeTriangle as AreaIcon } from 'react-icons/bi';
import { IoAnalyticsOutline as DistanceIcon } from 'react-icons/io5';
import { BsEraserFill as ErasorIcon } from 'react-icons/bs';
import { unByKey } from 'ol/Observable';

import { useProject } from '@contexts/Project.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { createOverlay, removeOverlay } from './utils/helpers';
import selectTool from './utils/selectTool';
import { KeyboardShortcut } from '../../SharedStyles';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';

/**
 * The available measurement tools, keyed by tool name.
 *
 * Module scope on purpose: the OpenLayers Style objects below would otherwise be
 * re-allocated on every render, and the shortcut hooks in the component depend on
 * this list having a fixed shape.
 *
 * @property {string} keyName Identifies the tool in component state.
 * @property {string} shortcutKey Keyboard key that selects the tool.
 * @property {JSX.Element} icon Glyph shown on the toggle button.
 * @property {string} type Geometry the tool draws, or Move/Erase for the two non-drawing tools.
 * @property {Style} [style] Style applied to the sketch and to the finished feature.
 */
const TOOLS = {
	Move: {
		keyName: 'Move',
		shortcutKey: 'M',
		icon: <MoveIcon className="mx-1" />,
		type: 'Move',
	},

	Area: {
		keyName: 'Area',
		shortcutKey: 'A',
		icon: <AreaIcon className="mx-1" />,
		type: 'Polygon',
		style: new Style({
			fill: new Fill({
				color: 'rgba(255, 255, 255, 0.2)',
			}),

			stroke: new Stroke({
				color: '#ffcc33',
				width: 2,
			}),

			image: new Circle({
				radius: 6,
				stroke: new Stroke({
					color: '#ffcc33',
				}),
			}),
		}),
	},

	Distance: {
		keyName: 'Distance',
		shortcutKey: 'D',
		icon: <DistanceIcon className="mx-1" />,
		type: 'LineString',
		style: new Style({
			stroke: new Stroke({
				color: '#ffcc33',
				lineCap: 'round',
				lineJoin: 'bevel',
				width: 2,
			}),

			image: new Circle({
				radius: 4,
				stroke: new Stroke({
					color: '#ffcc33',
				}),
			}),
		}),
	},

	Erase: {
		keyName: 'Erase',
		shortcutKey: 'E',
		icon: <ErasorIcon className="mx-1" />,
		type: 'Erase',
	},
};

/**
 * MeasurementTools — the draw tool picker shown in the bottom toolbar.
 *
 * Renders nothing until MeasurementLayer has published a layer to draw into,
 * so it appears and disappears with measurement mode without needing to know
 * about the mode itself.
 *
 * @param {Object} props Passed through to the toggle button group (styling, layout).
 * @returns {JSX.Element|null} The tool picker, or null while there is no layer.
 */
const MeasurementTools = ({ ...props }) => {
	const { mapObject, measurementLayer } = useProject();

	const [tool, setTool] = useState(TOOLS.Move);

	// Suspended while another tool owns the map: two Draw interactions would share
	// every click, committing that tool's boundary as a measurement too. Suspended
	// rather than unmounted, so the sketches and the selected tool survive.
	const { busy: suspended } = useMapToolBusy({ owner: 'measurement' });

	// The last term keeps a tool from re-selecting itself.
	const canSelect = ({ keyName }) => !!measurementLayer && !suspended && tool.keyName !== keyName;
	useKeyboardShortcut(TOOLS.Move.shortcutKey, canSelect(TOOLS.Move), () => setTool(TOOLS.Move));
	useKeyboardShortcut(TOOLS.Area.shortcutKey, canSelect(TOOLS.Area), () => setTool(TOOLS.Area));
	useKeyboardShortcut(TOOLS.Distance.shortcutKey, canSelect(TOOLS.Distance), () =>
		setTool(TOOLS.Distance)
	);
	useKeyboardShortcut(TOOLS.Erase.shortcutKey, canSelect(TOOLS.Erase), () =>
		setTool(TOOLS.Erase)
	);

	useEffect(() => {
		if (!measurementLayer || suspended) return;
		const { type, style } = tool;
		const sketchOverlay = createOverlay({
			mapObject: mapObject,
			type: type,
			className: 'ol-tooltip measurement-sketch',
		});

		const { interactions, keys } = selectTool({
			mapObject: mapObject,
			layer: measurementLayer,
			sketchOverlay: sketchOverlay,
			type: type,
			style: style,
			featureType: 'measurement',
		});

		// Cleanup
		return () => {
			// Removes Sketch Overlay
			removeOverlay({
				mapObject: mapObject,
				overlay: sketchOverlay,
			});

			// Removes Interactions
			interactions.forEach(interaction => mapObject.removeInteraction(interaction));

			// Removes Event Listeners
			keys.forEach(key => unByKey(key));

			// Resets cursor
			mapObject.getTargetElement().style.cursor = 'auto';
		};
	}, [tool, measurementLayer, suspended]);

	if (!mapObject || !measurementLayer) return null;
	return (
		<ToggleButtonGroup
			{...props}
			type="radio"
			name="measurement-tools"
			value={tool.keyName}
			onChange={toolName => setTool(TOOLS[toolName])}>
			{Object.entries(TOOLS).map(([toolName, { keyName, shortcutKey, icon }]) => {
				return (
					<ToggleButton
						key={`${toolName}-tool`}
						id={`radio-button-${toolName.toLowerCase()}`}
						value={toolName}
						disabled={suspended}
						variant={tool.keyName == keyName ? 'success' : 'dark'}>
						{icon}
						{toolName}
						<KeyboardShortcut>{shortcutKey.toUpperCase()}</KeyboardShortcut>
					</ToggleButton>
				);
			})}
		</ToggleButtonGroup>
	);
};

export default MeasurementTools;
